import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface PaymentConfig {
  stripeEnabled: boolean;
  cryptoEnabled: boolean;
  cryptoCurrency: string;
  cryptoNetwork: string;
  cryptoAddress: string;
  loading: boolean;
}

const DEFAULT_CONFIG: PaymentConfig = {
  stripeEnabled: true,
  cryptoEnabled: false,
  cryptoCurrency: 'USDT',
  cryptoNetwork: 'TRC20',
  cryptoAddress: '',
  loading: true,
};

export function usePaymentSettings(): PaymentConfig {
  const [config, setConfig] = useState<PaymentConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data, error } = await supabase
          .from('admin_settings')
          .select('setting_key, setting_value')
          .in('setting_key', [
            'payment_stripe_enabled',
            'payment_crypto_enabled',
            'payment_crypto_currency',
            'payment_crypto_network',
            'payment_crypto_address',
          ]);

        if (error) {
          console.error('Error fetching payment config:', error);
          setConfig(prev => ({ ...prev, loading: false }));
          return;
        }

        const partial: Partial<PaymentConfig> = {};
        data?.forEach((row) => {
          switch (row.setting_key) {
            case 'payment_stripe_enabled':
              partial.stripeEnabled = row.setting_value === 'true';
              break;
            case 'payment_crypto_enabled':
              partial.cryptoEnabled = row.setting_value === 'true';
              break;
            case 'payment_crypto_currency':
              partial.cryptoCurrency = row.setting_value || 'USDT';
              break;
            case 'payment_crypto_network':
              partial.cryptoNetwork = row.setting_value || 'TRC20';
              break;
            case 'payment_crypto_address':
              partial.cryptoAddress = row.setting_value || '';
              break;
          }
        });

        setConfig({
          ...DEFAULT_CONFIG,
          ...partial,
          // Only enable crypto if address is set
          cryptoEnabled: partial.cryptoEnabled === true && !!(partial.cryptoAddress),
          loading: false,
        });
      } catch (err) {
        console.error('Error fetching payment config:', err);
        setConfig(prev => ({ ...prev, loading: false }));
      }
    };

    fetchConfig();
  }, []);

  return config;
}
